import type { z } from 'zod';
import { externalNetworkClient } from '@terra/shared-rest-clients-external-network';
import { internalNetworkClient } from '@terra/shared-rest-clients-internal-network';
import {
  ExternalNetworkSignalListQuerySchema,
  ExternalNetworkSignalsCountQuerySchema,
} from '@terra/shared-contracts-external-network';
import type {
  AssetGraphQueryDto,
  ExternalNetworkContract,
} from '@terra/shared-contracts-external-network';
import {
  InternalNetworkSignalListQuerySchema,
  InternalNetworkSignalsCountQuerySchema,
} from '@terra/shared-contracts-internal-network';
import type {
  AdGraphQueryDto,
  InternalNetworkContract,
} from '@terra/shared-contracts-internal-network';
import { tenantAwareApiWrapper, withOrchestratorClient } from './api-core';
import { withSurfaceTarget } from './surface-routing';
import type {
  SurfaceFindingsListParams,
  SurfaceSignalsListParams,
  SurfaceSignalsListResult,
  SurfaceTargetId,
} from './types';
import {
  toExternalSurfaceSignalsListResult,
  toInternalSurfaceSignalsListResult,
} from './types';

const toInternalNetworkRouteParams = (surfaceId: SurfaceTargetId) => ({
  networkId: surfaceId,
});

const toSurfaceSignalsQueryPayload = ({
  entityId,
  entityKind,
  ...filters
}: Omit<SurfaceSignalsListParams, 'targetType' | 'surfaceId'> & {
  signalId?: string;
}) => ({
  ...filters,
  ...(entityId ? { targetId: entityId } : {}),
  ...(entityKind ? { entityKind } : {}),
});

export const surfaceApi = {
  getExternalNetworkGraph: (
    surfaceId: string,
    query?: Partial<AssetGraphQueryDto>,
  ) =>
    tenantAwareApiWrapper<typeof ExternalNetworkContract.getAssetGraph>({
      endpointFn: (headers) =>
        externalNetworkClient.getAssetGraph({
          params: { surfaceId },
          query,
          ...withOrchestratorClient(headers),
        }),
    }),

  searchExternalNetworkEntities: ({
    surfaceId,
    term,
    skip,
    limit,
  }: {
    surfaceId: string;
    term: string;
    skip?: number;
    limit?: number;
  }) =>
    tenantAwareApiWrapper<
      typeof ExternalNetworkContract.searchExternalNetworkEntities
    >({
      endpointFn: (headers) =>
        externalNetworkClient.searchExternalNetworkEntities({
          params: { surfaceId },
          query: { q: term, skip, limit },
          ...withOrchestratorClient(headers),
        }),
    }),

  getInternalNetworkGraph: (
    networkId: string,
    query: AdGraphQueryDto = { mode: 'full' },
  ) =>
    tenantAwareApiWrapper<typeof InternalNetworkContract.getGraph>({
      endpointFn: (headers) =>
        internalNetworkClient.getGraph({
          params: { networkId },
          query,
          ...withOrchestratorClient(headers),
        }),
    }),

  listSurfaceSignals: ({
    targetType,
    surfaceId,
    ...params
  }: SurfaceSignalsListParams): Promise<SurfaceSignalsListResult> => {
    const queryPayload = toSurfaceSignalsQueryPayload(params);

    return withSurfaceTarget<SurfaceSignalsListResult>({
      targetType,
      runInternal: async () => {
        const query = InternalNetworkSignalListQuerySchema.parse(
          queryPayload,
        ) as z.input<typeof InternalNetworkSignalListQuerySchema>;
        const result = await tenantAwareApiWrapper<
          typeof InternalNetworkContract.listSignals
        >({
          endpointFn: (headers) =>
            internalNetworkClient.listSignals({
              params: toInternalNetworkRouteParams(surfaceId),
              query,
              ...withOrchestratorClient(headers),
            }),
        });

        return toInternalSurfaceSignalsListResult(result);
      },
      runExternal: async () => {
        const query = ExternalNetworkSignalListQuerySchema.parse(
          queryPayload,
        ) as z.input<typeof ExternalNetworkSignalListQuerySchema>;
        const result = await tenantAwareApiWrapper<
          typeof ExternalNetworkContract.listSignals
        >({
          endpointFn: (headers) =>
            externalNetworkClient.listSignals({
              params: { surfaceId },
              query,
              ...withOrchestratorClient(headers),
            }),
        });

        return toExternalSurfaceSignalsListResult(result);
      },
    });
  },

  countSurfaceFindings: ({
    targetType,
    surfaceId,
    severity,
    status,
    signalId,
  }: Omit<SurfaceFindingsListParams, 'skip' | 'limit'>): Promise<number> => {
    const query = { severity, status, signalId, includeUnpublished: true };

    return withSurfaceTarget({
      targetType,
      runInternal: async () => {
        const result = await tenantAwareApiWrapper<
          typeof InternalNetworkContract.countFindings
        >({
          endpointFn: (headers) =>
            internalNetworkClient.countFindings({
              params: toInternalNetworkRouteParams(surfaceId),
              query,
              ...withOrchestratorClient(headers),
            }),
        });

        return result.count;
      },
      runExternal: async () => {
        const result = await tenantAwareApiWrapper<
          typeof ExternalNetworkContract.countFindings
        >({
          endpointFn: (headers) =>
            externalNetworkClient.countFindings({
              params: { surfaceId },
              query,
              ...withOrchestratorClient(headers),
            }),
        });

        return result.count;
      },
    });
  },

  countSurfaceSignals: ({
    targetType,
    surfaceId,
    ...params
  }: Omit<SurfaceSignalsListParams, 'skip' | 'limit'> & {
    signalId?: string;
  }): Promise<number> => {
    const queryPayload = toSurfaceSignalsQueryPayload(params);

    return withSurfaceTarget({
      targetType,
      runInternal: async () => {
        const query = InternalNetworkSignalsCountQuerySchema.parse(
          queryPayload,
        ) as z.input<typeof InternalNetworkSignalsCountQuerySchema>;
        const result = await tenantAwareApiWrapper<
          typeof InternalNetworkContract.countSignals
        >({
          endpointFn: (headers) =>
            internalNetworkClient.countSignals({
              params: toInternalNetworkRouteParams(surfaceId),
              query,
              ...withOrchestratorClient(headers),
            }),
        });

        return result.count;
      },
      runExternal: async () => {
        const query = ExternalNetworkSignalsCountQuerySchema.parse(
          queryPayload,
        ) as z.input<typeof ExternalNetworkSignalsCountQuerySchema>;
        const result = await tenantAwareApiWrapper<
          typeof ExternalNetworkContract.countSignals
        >({
          endpointFn: (headers) =>
            externalNetworkClient.countSignals({
              params: { surfaceId },
              query,
              ...withOrchestratorClient(headers),
            }),
        });

        return result.count;
      },
    });
  },
};
